import { useCallback, useEffect, useRef, useState } from 'react';

const MIN_THUMB = 40;
const ARROW_STEP = 56;
const REPEAT_MS = 120;

function UpArrow() {
  return (
    <svg
      width="9"
      height="6"
      viewBox="0 0 9 6"
      aria-hidden="true"
      style={{ filter: 'drop-shadow(0 1px 0 rgba(255, 255, 255, 0.6))' }}
    >
      <path d="M4.5 0 9 6H0z" fill="#101010" />
    </svg>
  );
}

function DownArrow() {
  return (
    <svg
      width="9"
      height="6"
      viewBox="0 0 9 6"
      aria-hidden="true"
      style={{ filter: 'drop-shadow(0 -1px 0 rgba(255, 255, 255, 0.6))' }}
    >
      <path d="M0 0h9L4.5 6z" fill="#101010" />
    </svg>
  );
}

export default function SPSSRetroScrollbar() {
  const [visible, setVisible] = useState(false);
  const trackRef = useRef(null);
  const thumbRef = useRef(null);
  const repeatRef = useRef(null);
  const dragRef = useRef(null);
  const rafRef = useRef(0);
  const metricsRef = useRef({ trackH: 0, maxOffset: 0, maxScroll: 0 });

  const update = useCallback(() => {
    const docEl = document.documentElement;
    const trackEl = trackRef.current;
    const thumbEl = thumbRef.current;
    const maxScroll = Math.max(0, docEl.scrollHeight - window.innerHeight);
    setVisible(maxScroll > 4);
    if (!trackEl || !thumbEl) return;
    const trackH = trackEl.clientHeight;
    const thumbH = Math.max(MIN_THUMB, (window.innerHeight / docEl.scrollHeight) * trackH);
    const maxOffset = Math.max(0, trackH - thumbH);
    const offset = maxScroll > 0 ? (window.scrollY / maxScroll) * maxOffset : 0;
    thumbEl.style.height = `${Math.round(thumbH)}px`;
    thumbEl.style.top = `${Math.round(offset)}px`;
    metricsRef.current = { trackH, maxOffset, maxScroll };
  }, []);

  const scheduleUpdate = useCallback(() => {
    if (rafRef.current) return;
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = 0;
      update();
    });
  }, [update]);

  useEffect(() => {
    update();
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);

    let ro;
    if (typeof ResizeObserver !== 'undefined' && document.body) {
      ro = new ResizeObserver(scheduleUpdate);
      ro.observe(document.body);
    }

    return () => {
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      if (ro) ro.disconnect();
      if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
      if (repeatRef.current) clearInterval(repeatRef.current);
    };
  }, [scheduleUpdate, update]);

  const handleThumbDown = (e) => {
    e.preventDefault();
    thumbRef.current?.setPointerCapture?.(e.pointerId);
    dragRef.current = {
      startClientY: e.clientY,
      startScrollY: window.scrollY,
      ratio:
        metricsRef.current.maxOffset > 0
          ? metricsRef.current.maxScroll / metricsRef.current.maxOffset
          : 1,
    };
  };

  const handleThumbMove = (e) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dy = e.clientY - drag.startClientY;
    window.scrollTo(0, drag.startScrollY + dy * drag.ratio);
  };

  const handleThumbUp = () => {
    dragRef.current = null;
  };

  const handleTrackDown = (e) => {
    if (e.target !== trackRef.current) return;
    const rect = trackRef.current.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height));
    window.scrollTo(0, ratio * metricsRef.current.maxScroll);
  };

  const startRepeat = (dir) => {
    window.scrollBy({ top: dir });
    if (repeatRef.current) clearInterval(repeatRef.current);
    repeatRef.current = setInterval(() => window.scrollBy({ top: dir }), REPEAT_MS);
  };

  const stopRepeat = () => {
    if (repeatRef.current) {
      clearInterval(repeatRef.current);
      repeatRef.current = null;
    }
  };

  if (!visible) return null;

  return (
    <div className="spss-retro-scrollbar">
      <button
        type="button"
        aria-label="Scroll up"
        className="spss-retro-scrollbar-btn spss-retro-scrollbar-btn-up"
        onPointerDown={() => startRepeat(-ARROW_STEP)}
        onPointerUp={stopRepeat}
        onPointerLeave={stopRepeat}
        onPointerCancel={stopRepeat}
      >
        <UpArrow />
      </button>

      <div ref={trackRef} className="spss-retro-scrollbar-track" onPointerDown={handleTrackDown}>
        <div
          ref={thumbRef}
          className="spss-retro-scrollbar-thumb"
          onPointerDown={handleThumbDown}
          onPointerMove={handleThumbMove}
          onPointerUp={handleThumbUp}
          onPointerCancel={handleThumbUp}
        />
      </div>

      <button
        type="button"
        aria-label="Scroll down"
        className="spss-retro-scrollbar-btn spss-retro-scrollbar-btn-down"
        onPointerDown={() => startRepeat(ARROW_STEP)}
        onPointerUp={stopRepeat}
        onPointerLeave={stopRepeat}
        onPointerCancel={stopRepeat}
      >
        <DownArrow />
      </button>

      <div className="spss-retro-scrollbar-base" />
    </div>
  );
}